import { useState, useEffect } from 'react';
import {
  Card, Row, Col, Statistic, Table, Tag, Button, Select, DatePicker, Space, Tabs, Progress, Empty, message,
} from 'antd';
import {
  DownloadOutlined, SearchOutlined, TrophyOutlined, ArrowUpOutlined, ArrowDownOutlined,
} from '@ant-design/icons';
import ReactEChartsCore from 'echarts-for-react';
import dayjs from 'dayjs';
import { reports } from '../../services/api';
import StorageCalcPanel from './StorageCalcPanel';

const TYPE_OPTIONS = [
  { value: 'daily', label: '日报' },
  { value: 'monthly', label: '月报' },
  { value: 'yearly', label: '年报' },
];

// 前三名奖杯颜色：金/银/铜
const TROPHY_COLORS = ['#FFD700', '#C0C0C0', '#CD7F32'];

// ============ Tab1：报表生成 ============
function GeneratePanel() {
  const [type, setType] = useState('daily');
  const [day, setDay] = useState(dayjs()); // 日报选单日
  const [range, setRange] = useState([dayjs().startOf('month'), dayjs()]); // 月报/年报选范围
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // 当前查询参数：日报 start=end=所选日期
  const currentParams = () => {
    if (type === 'daily') {
      const d = day.format('YYYY-MM-DD');
      return { type, start_date: d, end_date: d };
    }
    return {
      type,
      start_date: range[0].format('YYYY-MM-DD'),
      end_date: range[1].format('YYYY-MM-DD'),
    };
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const res = await reports.generate(currentParams());
      setReport(res.data);
    } catch (err) {
      // 拦截器已统一提示
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await reports.export(currentParams());
      message.success('报表已导出');
    } catch (err) {
      // 拦截器已统一提示
    } finally {
      setExporting(false);
    }
  };

  const s = report?.summary;
  // sections 中"表计用电统计"为按表计的明细
  const meterSection = report?.sections?.find((sec) => sec.title === '表计用电统计');

  const meterColumns = [
    { title: '表计名称', dataIndex: 'name' },
    { title: '平均功率(kW)', dataIndex: 'avg_power' },
    { title: '采样数', dataIndex: 'samples' },
  ];

  return (
    <Card
      title="报表生成"
      size="small"
      extra={(
        <Space wrap>
          <Select value={type} style={{ width: 100 }} options={TYPE_OPTIONS} onChange={setType} />
          {type === 'daily' ? (
            <DatePicker value={day} allowClear={false} onChange={(d) => d && setDay(d)} />
          ) : (
            <DatePicker.RangePicker
              value={range}
              allowClear={false}
              onChange={(r) => r && setRange(r)}
            />
          )}
          <Button type="primary" loading={loading} onClick={handleGenerate}>生成预览</Button>
          <Button icon={<DownloadOutlined />} loading={exporting} disabled={!report} onClick={handleExport}>
            导出 Excel
          </Button>
        </Space>
      )}
    >
      {!report ? (
        <Empty description="请选择报表类型与日期后点击生成预览" />
      ) : (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col span={4}><Card size="small"><Statistic title="总用电量" value={s.total_electricity} suffix="kWh" precision={2} /></Card></Col>
            <Col span={4}><Card size="small"><Statistic title="光伏发电量" value={s.total_pv_generation} suffix="kWh" precision={2} /></Card></Col>
            <Col span={4}><Card size="small"><Statistic title="最大需量" value={s.max_power_demand} suffix="kW" precision={2} /></Card></Col>
            <Col span={4}><Card size="small"><Statistic title="碳排放" value={s.carbon_emission} suffix="kgCO₂" precision={2} /></Card></Col>
            <Col span={4}><Card size="small"><Statistic title="自给率" value={s.self_sufficiency_rate} suffix="%" precision={2} /></Card></Col>
            <Col span={4}>
              <Card size="small">
                <Statistic
                  title="数据来源"
                  value={report.data_source === 'telemetry_records' ? '遥测数据' : '估算'}
                  valueStyle={{ fontSize: 16 }}
                />
              </Card>
            </Col>
          </Row>
          <Table
            title={() => '表计用电统计'}
            columns={meterColumns}
            dataSource={(meterSection?.meters || []).map((m, i) => ({ ...m, key: i }))}
            size="small"
            pagination={false}
          />
        </>
      )}
    </Card>
  );
}

// ============ Tab2：同环比分析 ============
function ComparisonPanel() {
  const [type, setType] = useState('daily');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (t) => {
    setLoading(true);
    try {
      const res = await reports.comparison({ type: t });
      setData(res.data);
    } catch (err) {
      // 拦截器已统一提示
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load('daily'); }, []);

  // 三根柱状图：去年同期 / 上期 / 本期
  const chartOption = () => ({
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: {
      type: 'category',
      data: [
        `去年同期\n${data.year_ago.start}~${data.year_ago.end}`,
        `上期\n${data.previous.start}~${data.previous.end}`,
        `本期\n${data.current.start}~${data.current.end}`,
      ],
      axisLabel: { interval: 0, fontSize: 11 },
    },
    yAxis: { type: 'value', name: 'kWh' },
    series: [{
      name: '总用电量',
      type: 'bar',
      barWidth: 60,
      data: [
        { value: data.year_ago.total, itemStyle: { color: '#8c8c8c' } },
        { value: data.previous.total, itemStyle: { color: '#fa8c16' } },
        { value: data.current.total, itemStyle: { color: '#1890ff' } },
      ],
      label: { show: true, position: 'top', formatter: '{c} kWh' },
    }],
  });

  // 变化率 Statistic：正红上升、负绿下降；null（对比期无数据）显示"数据积累中"
  const renderChange = (title, pct) => (
    <Card size="small">
      {pct == null ? (
        <Statistic title={title} value="数据积累中" valueStyle={{ fontSize: 16, color: '#999' }} />
      ) : (
        <Statistic
          title={title}
          value={Math.abs(pct)}
          suffix="%"
          precision={2}
          prefix={pct >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
          valueStyle={{ color: pct >= 0 ? '#ff4d4f' : '#52c41a' }}
        />
      )}
    </Card>
  );

  return (
    <Card
      title="同环比分析"
      size="small"
      extra={(
        <Select
          value={type}
          style={{ width: 100 }}
          options={[
            { value: 'daily', label: '日报' },
            { value: 'monthly', label: '月报' },
          ]}
          onChange={(t) => { setType(t); load(t); }}
        />
      )}
      loading={loading}
    >
      {!data ? (
        <Empty description="暂无数据" />
      ) : (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col span={8}><Card size="small"><Statistic title="本期总用电量" value={data.current.total} suffix="kWh" precision={2} /></Card></Col>
            <Col span={8}>{renderChange(type === 'daily' ? '环比（较昨日）' : '环比（较上月）', data.mom_change_pct)}</Col>
            <Col span={8}>{renderChange(type === 'daily' ? '同比（较去年今日）' : '同比（较去年同月）', data.yoy_change_pct)}</Col>
          </Row>
          <ReactEChartsCore option={chartOption()} style={{ height: 320 }} notMerge />
        </>
      )}
    </Card>
  );
}

// ============ Tab3：能耗排名 ============
function RankingPanel() {
  const [range, setRange] = useState([dayjs().startOf('month'), dayjs()]);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (r) => {
    setLoading(true);
    try {
      const res = await reports.ranking({
        start_date: r[0].format('YYYY-MM-DD'),
        end_date: r[1].format('YYYY-MM-DD'),
      });
      setList(res.data || []);
    } catch (err) {
      // 拦截器已统一提示
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load([dayjs().startOf('month'), dayjs()]); }, []);

  const maxPower = list.length ? list[0].avg_power : 0; // 已按 avg_power 降序，第一名为最大值

  const columns = [
    {
      title: '名次', dataIndex: 'rank', width: 80,
      render: (r) => (r <= 3
        ? <TrophyOutlined style={{ fontSize: 18, color: TROPHY_COLORS[r - 1] }} />
        : r),
    },
    { title: '表计名称', dataIndex: 'name' },
    { title: '类型', dataIndex: 'type', render: (t) => <Tag>{t}</Tag> },
    {
      title: '平均功率(kW)', dataIndex: 'avg_power',
      render: (v) => (
        <Space style={{ width: '100%' }}>
          <span style={{ minWidth: 70 }}>{v}</span>
          {/* 相对第一名的百分比 */}
          <Progress
            percent={maxPower > 0 ? parseFloat((v / maxPower * 100).toFixed(1)) : 0}
            size="small"
            style={{ width: 200 }}
            showInfo={false}
          />
        </Space>
      ),
    },
    { title: '采样数', dataIndex: 'samples' },
  ];

  return (
    <Card
      title="能耗排名"
      size="small"
      extra={(
        <Space>
          <DatePicker.RangePicker
            value={range}
            allowClear={false}
            onChange={(r) => r && setRange(r)}
          />
          <Button type="primary" icon={<SearchOutlined />} loading={loading} onClick={() => load(range)}>查询</Button>
        </Space>
      )}
    >
      <Table
        loading={loading}
        columns={columns}
        dataSource={list.map((m) => ({ ...m, key: m.meter_id }))}
        size="small"
        pagination={false}
        locale={{ emptyText: '该区间内暂无遥测数据' }}
      />
    </Card>
  );
}

// ============ Tab4：资方月报 ============
function AssetMonthlyPanel() {
  const [month, setMonth] = useState(dayjs());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const monthStr = month.format('YYYY-MM');

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const res = await reports.assetMonthly({ month: monthStr });
      setReport(res.data);
    } catch (err) {
      // 拦截器已统一提示
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await reports.assetMonthlyExport({ month: monthStr });
      message.success('资方月报已导出');
    } catch (err) {
      // 拦截器已统一提示
    } finally {
      setExporting(false);
    }
  };

  // 口径说明小字（估算/模拟数据诚实标注）
  const basisText = (text) => (
    <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>{text}</div>
  );

  const renderMom = (pct) => (
    pct == null
      ? <span style={{ color: '#999', fontSize: 14 }}>上月无数据</span>
      : (
        <span style={{ color: pct >= 0 ? '#ff4d4f' : '#52c41a' }}>
          {pct >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {Math.abs(pct)}%
        </span>
      )
  );

  // 峰平谷结构饼图
  const touPieOption = () => ({
    tooltip: { trigger: 'item', formatter: '{b}: {c} kWh ({d}%)' },
    legend: { bottom: 0 },
    series: [{
      type: 'pie',
      radius: ['40%', '65%'],
      center: ['50%', '45%'],
      data: [
        { name: '峰段', value: report.consumption.peak_kwh, itemStyle: { color: '#fa8c16' } },
        { name: '平段', value: report.consumption.flat_kwh, itemStyle: { color: '#1890ff' } },
        { name: '谷段', value: report.consumption.valley_kwh, itemStyle: { color: '#52c41a' } },
      ],
      label: { formatter: '{b}\n{d}%' },
    }],
  });

  // 收益构成柱状图
  const revenueBarOption = () => ({
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: ['需求响应补贴', '电力交易', '辅助服务', '储能套利(估算)', '考核扣款'], axisLabel: { interval: 0, fontSize: 11 } },
    yAxis: { type: 'value', name: '元' },
    series: [{
      type: 'bar',
      barWidth: 40,
      data: [
        { value: report.vpp.dr_subsidy_yuan, itemStyle: { color: '#1890ff' } },
        { value: report.vpp.trade_revenue_yuan, itemStyle: { color: '#52c41a' } },
        { value: report.vpp.service_revenue_yuan, itemStyle: { color: '#13c2c2' } },
        { value: report.storage.arbitrage_estimate_yuan, itemStyle: { color: '#faad14' } },
        { value: -report.vpp.penalty_yuan, itemStyle: { color: '#ff4d4f' } },
      ],
    }],
  });

  return (
    <Card
      title="资方月报"
      size="small"
      extra={(
        <Space wrap>
          <DatePicker
            picker="month"
            value={month}
            allowClear={false}
            onChange={(d) => d && setMonth(d)}
          />
          <Button type="primary" loading={loading} onClick={handleGenerate}>生成预览</Button>
          <Button icon={<DownloadOutlined />} loading={exporting} disabled={!report} onClick={handleExport}>
            导出 Excel
          </Button>
        </Space>
      )}
    >
      {!report ? (
        <Empty description="请选择月份后点击生成预览" />
      ) : (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Card size="small">
                <Statistic title="当月综合收益（含估算）" value={report.month_revenue_estimate_yuan} suffix="元" precision={2} />
                {basisText('VPP净收益 + 储能套利估算')}
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic title="光伏发电量" value={report.pv.generation_kwh} suffix="kWh" precision={2} />
                {basisText(<>环比 {renderMom(report.pv.mom_pct)}</>)}
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic title="总用电量" value={report.consumption.total_kwh} suffix="kWh" precision={2} />
                {basisText(<>环比 {renderMom(report.consumption.mom_pct)}</>)}
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic
                  title="数据来源"
                  value={report.data_source === 'telemetry_records' ? '遥测数据' : '估算'}
                  valueStyle={{ fontSize: 16 }}
                />
                {basisText(`统计周期 ${report.period.start} ~ ${report.period.end}`)}
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col span={12}>
              <Card size="small" title="发电侧（光伏）">
                <Row gutter={16}>
                  <Col span={12}><Statistic title="装机容量" value={report.pv.installed_capacity_kw} suffix="kW" precision={2} /></Col>
                  <Col span={12}>
                    {report.pv.equivalent_hours == null
                      ? <Statistic title="等效利用小时" value="无装机容量数据" valueStyle={{ fontSize: 16, color: '#999' }} />
                      : <Statistic title="等效利用小时" value={report.pv.equivalent_hours} suffix="h" precision={2} />}
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col span={12}>
              <Card size="small" title="储能侧">
                <Row gutter={16}>
                  <Col span={8}><Statistic title="充电量" value={report.storage.charge_kwh} suffix="kWh" precision={2} /></Col>
                  <Col span={8}><Statistic title="放电量" value={report.storage.discharge_kwh} suffix="kWh" precision={2} /></Col>
                  <Col span={8}><Statistic title="策略收益估算" value={report.storage.arbitrage_estimate_yuan} suffix="元" precision={2} /></Col>
                </Row>
                {basisText(report.storage.basis)}
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col span={12}>
              <Card size="small" title="用电侧 · 峰平谷结构">
                <ReactEChartsCore option={touPieOption()} style={{ height: 260 }} notMerge />
                {basisText(`电费估算 ${report.consumption.cost_estimate_yuan} 元 — ${report.consumption.basis}`)}
              </Card>
            </Col>
            <Col span={12}>
              <Card size="small" title="VPP 侧 · 收益构成">
                <ReactEChartsCore option={revenueBarOption()} style={{ height: 260 }} notMerge />
                {basisText(`需求响应参与 ${report.vpp.dr_event_count} 次，考核 ${report.vpp.dr_assessed_count} 次；VPP净收益 ${report.vpp.net_revenue_yuan} 元${report.vpp.data_source === 'no_data' ? '（本月暂无结算/考核数据）' : ''}`)}
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Card size="small" title="投资回报进度">
                {report.investment.progress_pct == null ? (
                  <Statistic title="回收进度" value="总投资额未设置" valueStyle={{ fontSize: 16, color: '#999' }} />
                ) : (
                  <>
                    <Progress percent={Math.min(report.investment.progress_pct, 100)} size="small" />
                    <Row gutter={16} style={{ marginTop: 8 }}>
                      <Col span={12}><Statistic title="总投资额" value={report.investment.total_investment_yuan} suffix="元" precision={2} /></Col>
                      <Col span={12}><Statistic title="累计收益" value={report.investment.cumulative_revenue_yuan} suffix="元" precision={2} /></Col>
                    </Row>
                  </>
                )}
                {basisText(report.investment.basis)}
              </Card>
            </Col>
            <Col span={12}>
              <Card size="small" title="环保效益">
                <Row gutter={16}>
                  <Col span={12}><Statistic title="CO₂减排" value={report.environment.co2_reduction_kg} suffix="kg" precision={2} /></Col>
                  <Col span={12}><Statistic title="等效植树" value={report.environment.equivalent_trees} suffix="棵" precision={1} /></Col>
                </Row>
                {basisText(report.environment.basis)}
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Card>
  );
}

export default function ReportCenter() {
  return (
    <Tabs
      items={[
        { key: 'generate', label: '报表生成', children: <GeneratePanel /> },
        { key: 'comparison', label: '同环比分析', children: <ComparisonPanel /> },
        { key: 'ranking', label: '能耗排名', children: <RankingPanel /> },
        { key: 'asset_monthly', label: '资方月报', children: <AssetMonthlyPanel /> },
        { key: 'storage_calc', label: '配储测算', children: <StorageCalcPanel /> },
      ]}
    />
  );
}
